import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import LoadingIndicator from './components/ui/LoadingIndicator';
import ErrorBoundary from './components/ErrorBoundary';

// Critical pages with direct import
import Login from './pages/Login';
import Register from './pages/Register';
import RegistrationClosed from './pages/RegistrationClosed';
import PublicHome from './pages/PublicHome';
import PrivateHome from './pages/PrivateHome';

// Lazy-loaded non-critical pages
const Profile = lazy(() => import('./pages/Profile'));
const Credits = lazy(() => import('./pages/Credits'));
//const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AffiliateDashboard = lazy(() => import('./pages/AffiliateDashboard'));
const Support = lazy(() => import('./pages/Support'));
const FAQ = lazy(() => import('./pages/FAQ'));
const News = lazy(() => import('./pages/News'));
const NewsArticle = lazy(() => import('./pages/NewsArticle'));
const CryptPage = lazy(() => import('./pages/CryptPage'));

// Loading fallback component
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
    <LoadingIndicator message="Loading page..." />
  </div>
);

// These route components must be inside App body where AuthProvider is available
// Home route component that redirects based on auth status
function HomeRoute() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <PrivateHome /> : <PublicHome />;
}

// Login route component that redirects if already authenticated
function LoginRoute() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Redirect to="/" /> : <Login />;
}

// Register route component that redirects if already authenticated
function RegisterRoute() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Redirect to="/" /> : <Register />;
}

function RegistrationClosedRoute() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Redirect to="/" /> : <RegistrationClosed />;
}

function App() {
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary title="Application Error" showReset={false}>
          <AuthProvider>
            <Layout>
              <ErrorBoundary title="Page Error" showReset={true}>
                <Suspense fallback={<PageLoader />}>
                  <Switch>
                    <Route exact path="/" component={HomeRoute} />
                    <Route path="/login" component={LoginRoute} />
                    <Route path="/register" component={RegistrationClosedRoute} />
                    <Route path="/faq" component={FAQ} />
                    <Route exact path="/news" component={News} />
                    <Route path="/news/:articleId" component={NewsArticle} />
                    <PrivateRoute path="/profile" component={Profile} />
                    <PrivateRoute path="/credits" component={Credits} />
                    <PrivateRoute path="/affiliate" component={AffiliateDashboard} />
                    <PrivateRoute path="/support" component={Support} />
                    <PrivateRoute path="/crypt" component={CryptPage} />
                    <Redirect to="/" />
                  </Switch>
                </Suspense>
              </ErrorBoundary>
            </Layout>
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </Router>
  );
}

export default App; 